import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { teamListApi } from '@/lib/bracket/shareApi';

const NEW = 'new';

export const SaveListDialog = ({ open, onOpenChange, teams, defaultName }) => {
  const [name, setName] = useState('');
  const [target, setTarget] = useState(NEW);
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(defaultName || 'My teams');
    setTarget(NEW);
    teamListApi.list().then((r) => setLists(r.lists)).catch(() => setLists([]));
  }, [open, defaultName]);

  const pick = (v) => {
    setTarget(v);
    const l = lists.find((x) => x.id === v);
    if (l) setName(l.name);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, teams };
      if (target === NEW) await teamListApi.create(body); else await teamListApi.update(target, body);
      toast.success(`Saved "${name.trim()}"`, { description: `${teams.length} teams. Use Fill teams to drop them into any bracket.` });
      onOpenChange(false);
    } catch (e) { toast.error('Could not save the list', { description: e.message }); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="save-team-list-dialog">
        <DialogHeader className="text-left">
          <DialogTitle className="text-2xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>SAVE TEAM LIST</DialogTitle>
          <DialogDescription>Saves the starting teams in order, so you can fill any bracket with them later.</DialogDescription>
        </DialogHeader>
        {lists.length > 0 && (
          <Select value={target} onValueChange={pick}>
            <SelectTrigger data-testid="save-team-list-target"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NEW} data-testid="save-team-list-target-new">Save as a new list</SelectItem>
              {lists.map((l) => <SelectItem key={l.id} value={l.id} data-testid={`save-team-list-target-${l.id}`}>Replace "{l.name}"</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="List name" data-testid="save-team-list-name" />
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="save-team-list-preview">
          {teams.length ? teams.map((t, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <span key={i} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-700">{i + 1}. {t}</span>
          )) : <p className="text-xs text-[#CF142B]">Type at least one starting team first.</p>}
        </div>
        <Button onClick={save} disabled={busy || !teams.length || !name.trim()} className="h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="save-team-list-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save {teams.length} teams
        </Button>
      </DialogContent>
    </Dialog>
  );
};
